<template>
  <tm-auto-size-panel-item
    :hiding-priority="hidingPriority"
    :is-after-more="isAfterMore"
  >
    <tm-button
      :to="to"
      :title="title"
      :disable="disabled"
      :href="href"
      :color="color"
      :icon-only="iconOnly"
      :flat="flat"
      :no-border="noBorder"
      :size="btnSize"
      :target="target"
      :skeleton="loading"
      :loading="submitLoading"
      :loading-color="submitLoadingColor"
      :loading-title="submitLoadingTitle"
      :class="buttonClass"
      :clicked="clicked"
      :tabindex="tabindex"
      @click="$emit('click', $event)"
    >
      <slot name="icon">
        <tm-icon
          v-if="icon"
          :name="icon"
          :size="iconSize"
          :left="!iconOnly"
          :rotate="iconRotate"
        />
      </slot>
      <div v-if="!iconOnly">{{ text }}</div>
      <tm-learning-tooltip
        v-if="isLearningTooltip && learningTooltipKey"
        :learning-tooltip-key="learningTooltipKey"
        :tooltip="String(tooltip)"
        :tooltip-learning="String(tooltipLearning)"
      />
      <tm-custom-tooltip v-else-if="tooltip">
        <span>{{ tooltip }}</span>
      </tm-custom-tooltip>
      <div
        v-if="badge"
        class="tm-auto-size-panel-item-btn__counter"
      >
        {{ badge }}
      </div>
    </tm-button>
    <template v-slot:dropdown-item>
      <tm-dropdown-item
        :title="title"
        :disable="disabled"
        :href="href"
        :icon-rotate="iconRotate"
        :icon="icon"
        :label="text"
        :to="to"
        :loading="loading"
        @click="$emit('click', $event)"
      />
    </template>
  </tm-auto-size-panel-item>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router'
import { defineAsyncComponent } from '@/composition/vue/compositionApi'
import type { IconName } from '@/assets/icons/icons'
import type { IconSize, ButtonColor, ButtonSize, IconRotate } from '@/components/shared/types'
import TmIcon from '@/components/shared/TmIcon.vue'
import TmButton from '@/components/shared/TmButton.vue'
import TmAutoSizePanelItem from '@/components/shared/autoSize/panel/TmAutoSizePanelItem.vue'
import TmDropdownItem from '@/components/shared/TmDropdownItem.vue'
import type { VueLikeClassAttr } from '@/components/types'
import type { AutoSizeHidingPriority, AutoSizeHidingPriorityAttributeValue } from '@/composition/autoSize'
import TmCustomTooltip from '@/components/shared/tooltip/TmCustomTooltip.vue'

const TmLearningTooltip = defineAsyncComponent(() => import('@/components/shared/tooltip/TmLearningTooltip.vue'))

const { iconSize = 'small', btnSize = '' } = defineProps<{
  icon?: IconName
  color?: ButtonColor
  iconSize?: IconSize
  iconRotate?: IconRotate
  btnSize?: ButtonSize
  flat?: boolean
  noBorder?: boolean
  iconOnly?: boolean
  text?: string
  to?: RouteLocationRaw
  disabled?: boolean
  tooltip?: string
  href?: string
  target?: string
  clicked?: boolean
  loading?: boolean
  submitLoading?: boolean
  submitLoadingColor?: string
  submitLoadingTitle?: string
  title?: string
  isLearningTooltip?: boolean
  learningTooltipKey?: string
  tooltipLearning?: string
  badge?: string
  buttonClass?: VueLikeClassAttr
  hidingPriority?: AutoSizeHidingPriority | AutoSizeHidingPriorityAttributeValue
  isAfterMore?: boolean
  tabindex?: string
}>()

const emits = defineEmits<{
  click: [event: PointerEvent]
}>()
</script>

<style lang="scss" scoped>
.tm-auto-size-panel-item-btn {
  &__counter {
    position: absolute;
    top: -8px;
    right: -5px;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 20px;
    height: 16px;
    font-size: $sm-font;
    font-weight: $semi-bold-font;
    line-height: 16px;
    text-align: center;
    color: $white;
    border-radius: 8px;
    background: $blue;

    > span {
      padding-bottom: 0.2px;
    }
  }
}
</style>
